'use client';

import { AiToolkitVideoPlayer } from '@app/(darkHeader)/ai/_shared/_components/AiToolkitVideoPlayer/AiToolkitVideoPlayer';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { VIDEO_EDIT_MODEL_OPTIONS } from '@app/(darkHeader)/ai/_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES } from '@app/(darkHeader)/ai/_shared/_context/types';
import { GenerationFlowSuccessLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowSuccessLayout';
import { PostGenerationActionSection } from '@app/(darkHeader)/ai/_shared/_layouts/PostGenerationActionSection';
import sendAiContentDownloadEvent from 'components/components/events/AiContentDownloadEvent';
import { type JSX, type SyntheticEvent, useCallback } from 'react';

import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@videoblocks/design-system';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

const VIDEO_EDIT_TABS = {
    EDITED: 'edited',
    ORIGINAL: 'original',
} as const;

/**
 * Post-generation view for the video editor.
 */
export const VideoEditSuccessView = (): JSX.Element => {
    const { submitStates } = useAiToolkitContext();
    const {
        editedVideoUrl,
        originalVideoUrl,
        aiContentId,
        lastAiContentId,
        aiContentType: submittedAiContentType,
        aiModelName: submittedAiModelName,
        startTime = 0,
    } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT];

    const eventAiContentType = submittedAiContentType ?? AiContentType.AI_VIDEO_EDIT;
    const eventAiModelName = submittedAiModelName ?? VIDEO_EDIT_MODEL_OPTIONS[0].value;

    const handleDownload = useCallback(() => {
        if (typeof window !== 'undefined') {
            void sendAiContentDownloadEvent({
                aiContentId,
                voiceId: null,
                aiContentVoiceoverType: null,
                currentUrl: window.location.href,
                referrer: document.referrer || undefined,
                lastAiContentId,
                aiModelName: eventAiModelName,
                aiContentType: eventAiContentType,
            });
        }
    }, [aiContentId, lastAiContentId, eventAiContentType, eventAiModelName]);

    // Seeking is only honored once the duration is known, so this waits for metadata rather than
    // setting `currentTime` on mount. The tab unmounts when deselected, so reopening it replays
    // from the clip start instead of resuming where the last visit left off.
    const seekToClipStart = useCallback(
        (event: SyntheticEvent<HTMLVideoElement>): void => {
            event.currentTarget.currentTime = startTime;
        },
        [startTime],
    );

    return (
        <GenerationFlowSuccessLayout
            title="It's ready! Preview and download your edited video below."
            subtitle="A copy of your video has also been sent to your email."
        >
            <TabsRoot defaultValue={VIDEO_EDIT_TABS.EDITED} className="w-full gap-3">
                <TabsList className="h-auto gap-2 self-center bg-transparent">
                    <VideoEditTabTrigger value={VIDEO_EDIT_TABS.EDITED}>Edited</VideoEditTabTrigger>
                    <VideoEditTabTrigger value={VIDEO_EDIT_TABS.ORIGINAL}>Original</VideoEditTabTrigger>
                </TabsList>
                <TabsContent value={VIDEO_EDIT_TABS.EDITED}>
                    <AiToolkitVideoPlayer state="playback" src={editedVideoUrl} className="max-h-125" />
                </TabsContent>
                <TabsContent value={VIDEO_EDIT_TABS.ORIGINAL}>
                    <AiToolkitVideoPlayer
                        state="playback"
                        src={originalVideoUrl}
                        className="max-h-125"
                        onLoadedMetadata={seekToClipStart}
                    />
                </TabsContent>
            </TabsRoot>
            <PostGenerationActionSection
                downloadUrl={editedVideoUrl}
                downloadFilename="edited-video.mp4"
                downloadButtonText="Download Edit"
                onDownload={handleDownload}
                category={AI_TOOLKIT_CATEGORIES.VIDEO_EDIT}
                aiContentId={aiContentId}
                lastAiContentId={lastAiContentId}
                aiContentType={eventAiContentType}
                aiModelName={eventAiModelName}
            />
        </GenerationFlowSuccessLayout>
    );
};

/* ——— Subcomponents —————————————————————————————————————————————————————————— */

/**
 * `flex-none` keeps the two pills label-sized — the shared trigger defaults to `flex-1`, which
 * would stretch them across the row.
 */
function VideoEditTabTrigger({ value, children }: { value: string; children: string }): JSX.Element {
    return (
        <TabsTrigger
            value={value}
            fontSize="sm"
            fontWeight="semibold"
            className="flex-none data-[state=active]:bg-neutral-dark-200"
        >
            {children}
        </TabsTrigger>
    );
}
